import { useState } from 'react';

export interface TenantStepProps {
  initialDomain: string;
  onProbe: (domain: string) => Promise<
    | {
        kind: 'ok';
        tenantId: string;
        tenantDisplayName: string | null;
      }
    | { kind: 'not-found'; message: string }
  >;
  onConfirm: (args: {
    domain: string;
    tenantId: string;
    tenantDisplayName: string | null;
  }) => void;
}

export function TenantStep({
  initialDomain,
  onProbe,
  onConfirm,
}: TenantStepProps) {
  const [domain, setDomain] = useState(initialDomain);
  const [probing, setProbing] = useState(false);
  const [result, setResult] = useState<
    | null
    | {
        kind: 'ok';
        tenantId: string;
        tenantDisplayName: string | null;
      }
    | { kind: 'not-found'; message: string }
  >(null);

  async function handleProbe() {
    setProbing(true);
    setResult(null);
    try {
      const r = await onProbe(domain.trim());
      setResult(r);
    } finally {
      setProbing(false);
    }
  }

  function handleConfirm() {
    if (result?.kind === 'ok') {
      onConfirm({
        domain: domain.trim(),
        tenantId: result.tenantId,
        tenantDisplayName: result.tenantDisplayName,
      });
    }
  }

  return (
    <section
      data-testid="onboard-tenant-step"
      className="flex flex-col gap-4 p-4"
    >
      <h2 className="text-lg font-bold text-black">Find your tenant</h2>
      <p className="text-sm text-black">
        Enter your firm's primary domain (the one you use for email). We'll
        look it up at Microsoft to confirm.
      </p>

      <label className="flex flex-col gap-1 text-sm text-black">
        <span className="font-bold">Primary domain</span>
        <input
          type="text"
          data-testid="onboard-input-tenantDomain"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder="acme.com"
          className="border border-[#404040] bg-white px-2 py-1 font-mono text-black"
        />
      </label>

      <button
        type="button"
        data-testid="onboard-tenant-probe"
        onClick={handleProbe}
        disabled={probing || domain.trim().length === 0}
        className="self-start border border-[#404040] bg-[#d0d0d0] px-3 py-1 text-sm text-black hover:bg-[#e0e0e0] disabled:opacity-50"
      >
        {probing ? 'Looking up…' : 'Look up tenant'}
      </button>

      {result?.kind === 'ok' && (
        <div
          data-testid="onboard-tenant-result-ok"
          className="border border-[#080] bg-[#efe] p-3 text-sm text-black"
        >
          <p>
            Found{' '}
            <strong>{result.tenantDisplayName ?? domain.trim()}</strong>
          </p>
          <p className="mt-1 font-mono text-xs text-gray-700">
            tenant id: {result.tenantId}
          </p>
          <button
            type="button"
            data-testid="onboard-tenant-confirm"
            onClick={handleConfirm}
            className="mt-3 border border-[#404040] bg-[#d0d0d0] px-4 py-1 text-sm font-bold text-black cursor-pointer hover:bg-[#e0e0e0]"
          >
            That's correct — continue
          </button>
        </div>
      )}

      {result?.kind === 'not-found' && (
        <div
          data-testid="onboard-tenant-result-not-found"
          className="border border-[#a00] bg-[#fee] p-3 text-sm text-black"
        >
          <p>Couldn't find a tenant for that domain.</p>
          <p className="mt-1 text-xs text-gray-700">{result.message}</p>
        </div>
      )}
    </section>
  );
}
